import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";

interface ColumnVisibilityDrawerProps {
  open: boolean;
  onClose: () => void;
  hasProgramVersionItems: boolean;
  columnVisibility: ReturnType<
    typeof import("./ChallengesDetailedTable/ChallengesDetailedTable.columnVisibility").useColumnVisibility
  >;
}

const ColumnVisibilityDrawer: React.FC<ColumnVisibilityDrawerProps> = ({
  open,
  onClose,
  hasProgramVersionItems,
  columnVisibility,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 350 } }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Visibilidad de Columnas</Typography>
          <IconButton size="small" onClick={onClose}>
            <ClearIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {hasProgramVersionItems && (
          <List dense>
            {Object.entries(columnVisibility.groupedByProgram)
              .sort(([, a], [, b]) => a.name.localeCompare(b.name))
              .map(([programId, program]) => {
                const isProgramExpanded = columnVisibility.expandedLevels.has(`program_${programId}`);
                const programVisible = columnVisibility.isProgramVisible(programId);
                const programPartial = columnVisibility.isProgramPartiallyVisible(programId);
                return (
                  <React.Fragment key={programId}>
                    <ListItem
                      sx={{
                        px: 0, py: 0.5, backgroundColor: "action.selected",
                        borderRadius: 1, mb: 0.5, "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <Checkbox
                        checked={programVisible}
                        indeterminate={programPartial}
                        onChange={() => columnVisibility.handleToggleProgram(programId)}
                        size="small"
                      />
                      <ListItemText primary={program.name} primaryTypographyProps={{ fontWeight: 700, fontSize: "1rem" }} />
                      <IconButton size="small" onClick={() => columnVisibility.handleToggleLevelExpand(`program_${programId}`)}>
                        {isProgramExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </ListItem>
                    <Collapse in={isProgramExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding dense>
                        {Object.entries(program.levels)
                          .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                          .map(([levelId, level]) => {
                            const isLevelExpanded = columnVisibility.expandedLevels.has(`${programId}_${levelId}`);
                            const levelVisible = columnVisibility.isProgramLevelVisible(programId, levelId);
                            const levelPartial = columnVisibility.isProgramLevelPartiallyVisible(programId, levelId);
                            return (
                              <React.Fragment key={levelId}>
                                <ListItem sx={{ pl: 2, py: 0.5, "&:hover": { backgroundColor: "action.hover" } }}>
                                  <Checkbox
                                    checked={levelVisible}
                                    indeterminate={levelPartial}
                                    onChange={() => columnVisibility.handleToggleProgramLevel(programId, levelId)}
                                    size="small"
                                  />
                                  <ListItemText primary={level.name} primaryTypographyProps={{ fontWeight: 600, fontSize: "0.95rem" }} />
                                  <IconButton size="small" onClick={() => columnVisibility.handleToggleLevelExpand(`${programId}_${levelId}`)}>
                                    {isLevelExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                </ListItem>
                                <Collapse in={isLevelExpanded} timeout="auto" unmountOnExit>
                                  <List component="div" disablePadding dense>
                                    {level.items.map((pvi) => {
                                      const field = `pvi_${pvi.id}`;
                                      const isVisible = columnVisibility.columnVisibilityModel[field] !== false;
                                      return (
                                        <ListItem key={pvi.id} sx={{ pl: 6, py: 0.5, "&:hover": { backgroundColor: "action.hover" } }}>
                                          <Checkbox
                                            checked={isVisible}
                                            onChange={() => columnVisibility.handleToggleColumn(field)}
                                            size="small"
                                          />
                                          <ListItemText primary={`${pvi.cpNumber}: ${pvi.name}`} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                                        </ListItem>
                                      );
                                    })}
                                  </List>
                                </Collapse>
                              </React.Fragment>
                            );
                          })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default ColumnVisibilityDrawer;
